import { JobSelectionRef } from '../../state/selection/selection.state'
import { SegObjectPropertyMap } from 'src/app/state/case/case.state'

export type { JobSelectionRef }

export enum PipelineType {
    Cubing = 'CUB',
    Segmentation = 'SEG',
    /** Frontend-only: routes to Custom Segmentation UI; job is still submitted as Segmentation (SEG). */
    CustomSegmentation = 'SEG_CUSTOM',
    NucleusExpansion = 'EXP',
    // todo: the registration type exists for backwards compatibility and can probably be removed later on
    Registration = 'REG',
    mIHC = 'MIH',
    cycIF = 'CYC',
    MapIntensity = 'INT',
    RecurrentNeighborhoods = 'RCN',
    NeighborhoodEnrichment = 'NEN',
    AutoCellDetection = 'ACD',
    Clustering = 'CLU',
    tsneClustering = 'TSN',
    umapClustering = 'UMA',
    Plotting = 'PLT',
    AddMappingProperties = 'ADP',
}


export interface NucleusExpansionParameters {
    type: PipelineType.NucleusExpansion
    target: string | undefined
    batch: boolean
}

export interface RegistrationParameters {
    type: PipelineType.mIHC | PipelineType.cycIF
    target: string | undefined
    source: (string | undefined)[]
    batch: boolean
}

export interface IntensityParameters {
    type: PipelineType.MapIntensity
    target: string | undefined
    mask: string | undefined
    batch: boolean
    segSource?: SegObjectPropertyMap | undefined
    radius?: number
    summary?: string
}

export interface RecurrentNeighborhoodsParameters {
    // TODO [ARIADNE-292]: reuse RecurrentNeighborhoodsResult details here?
    type: PipelineType.RecurrentNeighborhoods
    selections: (JobSelectionRef | undefined)[]
    experimentName: string
    keepBackground: boolean
    radius: number
    numberOfRcn: number
    batch: boolean
}
export interface NeighborhoodEnrichmentParameters {
    type: PipelineType.NeighborhoodEnrichment
    selections: (JobSelectionRef | undefined)[]
    experimentName: string
    keepBackground: boolean
    radius: number
    batch: boolean
}

export interface TsneParameters {
    type: PipelineType.tsneClustering
    markers: string[]
    perplexity: number
    numberOfIterations: number
    normalization: ClusteringNormalization
    seg_object_property_maps?: SegObjectPropertyMap[]
}

export interface UmapParameters {
    type: PipelineType.umapClustering
    markers: string[]
    numberOfNeighbors: number
    minimumDistance: number
    spread: number
    normalization: ClusteringNormalization
    seg_object_property_maps?: SegObjectPropertyMap[]
}

export interface PlottingParameters {
    type: PipelineType.Plotting
    plotType: string
    name: string
    groups: any[][]
    cohorts: { id: number; name: string }[]
    properties: string[]
    details: any[]
    segSource: string
}

export type ClusteringParametersWithName = { name: string } & (UmapParameters | TsneParameters)

export enum SegmentationType {
    Cell = 'cell',
    Nucleus = 'nucleus',
    Tissue = 'tissue',
    Artifacts = 'artifacts',
    Plaque = 'plaque',
    Pathology = 'pathology',
}

export enum ModelType {
    Cellpose = 'cellpose',
    UNet = 'unet',
}

export interface ModelInput {
    name: string
    required?: boolean
}

export interface OutputClass {
    name: string
    type: string
}

export interface SegmentationModel {
    id: number
    display_name: string
    description?: string
    input_channels?: ModelInput[]
    channel_order?: string
    output_classes?: OutputClass[]
    /** @deprecated use display_name; kept for backward compatibility */
    name?: string
    type?: ModelType
    inputs?: ModelInput[]
}

export interface CellposeModelInputs {
    membrane?: string[]
    nucleus?: string[]
}

/** Combination method for multi-layer inputs (SEG Auto). Only sent when input has > 1 layer. */
export type SegmentationCombinationMethod = 'median' | 'max' | 'min' | 'sum'

/** One input channel for SEG Auto: layers (display_name[]) and optional combination when > 1 layer. */
export interface SegmentationInputItem {
    input_name: string
    layers: string[]
    combination_method?: SegmentationCombinationMethod
}

export interface SegmentationParameters {
    type: PipelineType.Segmentation
    model_id: number
    batch: boolean
    /** Per model input: layers (display_name[]) and optional combination_method when > 1 layer. */
    inputs?: SegmentationInputItem[]
    /** Full result / issue label: "{model display_name} - {user suffix}". */
    result_display_name: string
}

export interface CustomSegmentationParameters {
    type: PipelineType.Segmentation
    /** Task: predefined (cell, nucleus, tissue, artifacts, plaque, pathology) or custom string. */
    task: SegmentationType | string
    batch: boolean
    /** For task "cell": membrane layer display_names. */
    membrane?: string[]
    /** For task "cell": nucleus layer display_names (optional). */
    nucleus?: string[]
    /** For non-cell tasks: input channel layer display_names. */
    channel_layers?: string[]
    /** Description; always send, use "" when the user leaves it empty. */
    description: string
}

export enum JobStatus {
    Rejected = 'Rejected',
    Closed = 'Closed',
    // all other status values are mapped to the yellow icon, such as New, Resolved, Feedback, ...
    // there are many more status values possible, but do not have to be integrated currently
}

export enum ClusteringNormalization {
    PerFeature = 'Per Feature',
    None = 'None',
    Global = 'Global',
}

export enum UmapDistanceMetric {
    Euclidean = 'Euclidean',
    Correlation = 'Correlation',
}

export interface Job {
    id: number
    name: string
    type: PipelineType
    status: JobStatus
    startDate: string
    estimatedHours: number | null
    progress: number
    dataset?: string
}
